import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { Scope } from '@lyceo/lyceo-pupil-app/practise/util-models';
import { BookSelectModalActions, ScopeActions } from './scope.actions';
import { CallState, LoadingState } from '@lyceo/shared/util-loading';
import { PractiseOverviewPageActions } from '../practise/practise.actions';
import { PersonUsesBookActions } from '../person-uses-book/person-uses-book.actions';
import { mapScope } from './scope.functions';
import { SettingsActions } from '@lyceo/lyceo-pupil-app/user/data-access';
import { AuthActions } from '@lyceo/shared/auth/data-access';

export const SCOPE_FEATURE_KEY: string = 'scope';

export interface ScopeState extends EntityState<Scope> {
  personScopesCallState: CallState;
  bookSelectModalScopeId: string | undefined;
}

export const scopeAdapter: EntityAdapter<Scope> = createEntityAdapter<Scope>();

export const initialScopeState: ScopeState = scopeAdapter.getInitialState({
  personScopesCallState: LoadingState.INIT,
  bookSelectModalScopeId: undefined,
});

const reducer: ActionReducer<ScopeState, Action> = createReducer(
  initialScopeState,
  on(AuthActions.logoutSuccess, (): ScopeState => initialScopeState),
  on(
    SettingsActions.updateLevelYearSuccess,
    (state): ScopeState =>
      scopeAdapter.removeAll({
        ...state,
        personScopesCallState: LoadingState.INIT,
      }),
  ),
  on(
    ScopeActions.getPersonScopes,
    (state): ScopeState => ({
      ...state,
      personScopesCallState: LoadingState.LOADING,
    }),
  ),
  on(
    ScopeActions.getPersonScopesSuccess,
    (state, action): ScopeState =>
      scopeAdapter.upsertMany(
        action.scopes.map((scopeDto) => mapScope(scopeDto)),
        {
          ...state,
          personScopesCallState: LoadingState.LOADED,
        },
      ),
  ),
  on(
    ScopeActions.getPersonScopesFailure,
    (state, { error }): ScopeState => ({
      ...state,
      personScopesCallState: { errorMsg: error.message },
    }),
  ),
  on(
    PractiseOverviewPageActions.selectBookClicked,
    (state, action): ScopeState => ({
      ...state,
      bookSelectModalScopeId: action.scopeId,
    }),
  ),
  on(
    BookSelectModalActions.bookSelected,
    BookSelectModalActions.modalClosed,
    (state): ScopeState => ({
      ...state,
      bookSelectModalScopeId: undefined,
    }),
  ),
  on(
    ScopeActions.switchBookSuccess,
    (state, action): ScopeState =>
      scopeAdapter.upsertOne(mapScope(action.scope), {
        ...state,
      }),
  ),
  on(
    ScopeActions.switchBooksSuccess,
    (state, action): ScopeState =>
      scopeAdapter.upsertMany(
        action.scopes.map((scope) => mapScope(scope)),
        {
          ...state,
        },
      ),
  ),
  on(
    PersonUsesBookActions.getLatestPersonUsesBooksSuccess,
    (state, action): ScopeState =>
      scopeAdapter.upsertMany(
        action.personUsesBooks.map((personUsesBook) =>
          mapScope(personUsesBook.scope),
        ),
        {
          ...state,
        },
      ),
  ),
);

export function scopeReducer(
  state: ScopeState | undefined,
  action: Action,
): ScopeState {
  return reducer(state, action);
}
